import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { publishScheduledEmail } from '@/store/slices/scheduled-emails';
import { selectUpdatingEmail } from '@/store/slices/scheduled-emails/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import type { selectWorkingProject } from '@/store/slices/project/selectors';
import { selectUser } from '@/store/slices/user/selectors';
import { EmailTypeEnum, type IScheduledEmail } from '@/types/email.types';
import { SendTestEmail } from '@/connection/scheduled-emails/scheduled-emails';
import toastTrigger from '@ui/toast/toast-trigger';
import useLanguageParams from '@/utils/use-language-params';

type WorkingProject = ReturnType<typeof selectWorkingProject>;

interface UseCampaignPublishActionsArgs {
  scheduledEmail: IScheduledEmail | undefined;
  workingProject: WorkingProject;
  onPublished: () => void;
}

export function useCampaignPublishActions({
  scheduledEmail,
  workingProject,
  onPublished,
}: UseCampaignPublishActionsArgs) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const updatingEmail = useAppSelector(selectUpdatingEmail);
  const language = useLanguageParams();
  const [sendingTest, setSendingTest] = useState(false);

  const getFinal = useCallback(() => {
    if (!scheduledEmail) return;

    const customizations = {
      v2: {
        ...scheduledEmail.customizations.v2,
      },
    };

    const working_customizations = {
      ...(scheduledEmail.working_customizations ?? scheduledEmail.customizations),
      v2: {
        ...(scheduledEmail.working_customizations?.v2 ?? scheduledEmail.customizations.v2),
      },
    };

    return {
      ...scheduledEmail,
      customizations: {
        ...customizations,
        ...working_customizations,
      },
      settings: {
        ...(scheduledEmail.settings ?? {}),
        allInvites: scheduledEmail.email_type !== EmailTypeEnum.event_registration_email,
      },
    };
  }, [scheduledEmail]);

  const handlePublish = useCallback(async () => {
    const emailToSave = getFinal();

    if (emailToSave) {
      const result = await dispatch(publishScheduledEmail(emailToSave));

      if (emailToSave.email_type === EmailTypeEnum.event_registration_email && workingProject?.registration_settings) {
        await dispatch(
          saveWorkingProjectByFields({
            registration_settings: {
              ...workingProject.registration_settings,
              registration_confirmation_email: emailToSave.customizations,
            },
          })
        );
      }

      if (emailToSave.sent) {
        toastTrigger('Already sent', {
          description:
            'The email was saved but it has already been sent and cannot be resent. Please create a new campaign.',
          type: 'warning',
        });
      }

      if (publishScheduledEmail.fulfilled.match(result)) {
        onPublished();
      }
    }
  }, [dispatch, getFinal, onPublished, workingProject?.registration_settings]);

  const sendTest = useCallback(async () => {
    const email = getFinal();
    if (!email || !user || !workingProject) return;
    try {
      setSendingTest(true);
      await SendTestEmail([user.email], email, workingProject.uuid, workingProject.channel, language ?? 'en');
      toastTrigger('Test sent', {
        description: `Test email sent to ${user.email}`,
      });
    } catch (e) {
      console.error(e);
      toastTrigger('Error sending test', {
        description: 'We ran into an issue sending that email. Please try again later.',
        type: 'error',
      });
    } finally {
      setSendingTest(false);
    }
  }, [getFinal, user, workingProject, language]);

  return { updatingEmail, sendingTest, handlePublish, sendTest };
}
